import type { TrackEventHandler } from "@sg-protect/shell";

export interface DOMEventTelemetryHandler<T extends keyof DocumentEventMap> {
    isHandling: (evt: DocumentEventMap[T]) => boolean;
    handleEvent: (evt: DocumentEventMap[T], onEvent: TrackEventHandler) => void;
}

export class DOMEventTracker<T extends keyof DocumentEventMap> {
    private handlers: DOMEventTelemetryHandler<T>[] = [];
    private eventType: T;
    private isInitialized = false;
    private trackEvent: TrackEventHandler | null = null;
    // We need to keep a reference to the bound function to be able to remove it later
    // we also can't do document.addEventListener(this.eventType, this.handleEvent, { capture: true });
    // since 'this' would be undefined in that case
    private boundHandleEvent: ((evt: DocumentEventMap[T]) => DocumentEventMap[T]) | null = null;

    constructor(eventType: T) {
        this.eventType = eventType;
    }

    init(trackEvent: TrackEventHandler) {
        this.trackEvent = trackEvent;
        if (!this.isInitialized) {
            this.boundHandleEvent = evt => this.handleEvent(evt);
            document.addEventListener(this.eventType, this.boundHandleEvent, { capture: true });
            this.isInitialized = true;
        }
    }

    remove() {
        if (this.isInitialized && this.boundHandleEvent) {
            document.removeEventListener(this.eventType, this.boundHandleEvent, { capture: true });
            this.trackEvent = null;
            this.boundHandleEvent = null;
            this.isInitialized = false;
        }
    }

    addHandler(handler: DOMEventTelemetryHandler<T>) {
        this.handlers = [...this.handlers, handler];
    }

    private handleEvent(evt: DocumentEventMap[T]) {
        if (this.isInitialized) {
            this.handlers.forEach(handler => {
                if (handler.isHandling(evt)) {
                    handler.handleEvent(evt, this.trackEvent!);
                }
            });
        }

        return evt;
    }
}
